import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Stack,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "pages/components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/DashboardNavbar";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { UserQuotationApiService } from "api/userQuotation.api";
import { QUOTATION_STATUS_LABELS, INVOICE_STATUS_LABELS } from "config/statusLabels";
import EnhancedTableHead from "pages/components/common/EnhancedTableHead";
import { FILE_URL } from "config/constants";

// ─── Helpers ──────────────────────────────────────────────────────────────────

// A flat line carries its own term — a length or a fixed finish — snapshotted
// from the plan when it was quoted.
const subscriptionTermText = (item) => {
  if (item?.subscriptionTerm !== "flat") return "";
  if (item?.subscriptionTermMonths) {
    const m = item.subscriptionTermMonths;
    return m % 12 === 0
      ? ` · ${m / 12} ${m / 12 === 1 ? "year" : "years"}`
      : ` · ${m} ${m === 1 ? "month" : "months"}`;
  }
  if (item?.subscriptionEndDate)
    return ` · until ${String(item.subscriptionEndDate).slice(0, 10)}`;
  return "";
};

const formatCurrency = (v) => `$${Number(v || 0).toFixed(2)}`;
const formatDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
};

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" py={0.75} borderBottom="1px solid #f0f0f0">
    <SoftTypography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
      {label}
    </SoftTypography>
    <SoftTypography variant="body2" fontWeight="medium" textAlign="right">
      {value || "-"}
    </SoftTypography>
  </Box>
);

InfoRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const UserQuotationDetails = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPODialog, setShowPODialog] = useState(false);
  const [poForm, setPoForm] = useState({ poNumber: "", paymentTerms: "", note: "" });
  const [processingPayment, setProcessingPayment] = useState(false);

  const loadQuotation = async () => {
    try {
      setLoading(true);
      const response = await UserQuotationApiService.getQuotation(quoteId);
      setQuotation(response?.data?.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load quotation details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (quoteId) {
      loadQuotation();
    }
  }, [quoteId]);

  const handleAcceptClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
  };

  const handleAcceptSubmit = async () => {
    try {
      setShowConfirmModal(false);
      setAccepting(true);
      await UserQuotationApiService.acceptQuotation(quoteId);
      toast.success("Quotation accepted successfully!");
      loadQuotation(); // Refresh data
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to accept quotation.");
    } finally {
      setAccepting(false);
    }
  };

  const handlePOPayClick = () => {
    setPoForm({
      poNumber: quotation.customerPoNumber || "",
      paymentTerms: quotation.paymentTerms || "",
      note: "",
    });
    setShowPODialog(true);
  };

  const handlePOFormChange = (e) => {
    const { name, value } = e.target;
    setPoForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePOSubmit = async () => {
    if (!poForm.poNumber) {
      toast.error("PO Number is required.");
      return;
    }
    try {
      setProcessingPayment(true);
      await UserQuotationApiService.confirmWithPO(quoteId, poForm);
      toast.success("PO confirmed and Invoice generated!");
      setShowPODialog(false);
      loadQuotation();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to confirm PO.");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleStripePay = () => {
    navigate(`/user/quotations/${quoteId}/online-checkout`);
  };

  const handleViewPdf = () => {
    if (!quotation?.invoiceId?.invoicePdf) return;
    window.open(`${FILE_URL}/${quotation.invoiceId.invoicePdf}`, "_blank");
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card sx={{ p: 3, mb: 3 }}>
                <Skeleton variant="text" width={250} height={42} animation="wave" />
                <Skeleton variant="text" width={280} height={24} animation="wave" />
              </Card>
              <Card sx={{ p: 3, mb: 3 }}>
                <Skeleton variant="text" width={140} height={30} animation="wave" />
                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={12} md={6}>
                    {["org", "email", "so", "status"].map((rowKey) => (
                      <Skeleton
                        key={`user-info-skeleton-${rowKey}`}
                        variant="text"
                        height={28}
                        animation="wave"
                      />
                    ))}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {["line1", "line2", "city", "country"].map((rowKey) => (
                      <Skeleton
                        key={`user-address-skeleton-${rowKey}`}
                        variant="text"
                        height={28}
                        animation="wave"
                      />
                    ))}
                  </Grid>
                </Grid>
              </Card>
              <Card sx={{ p: 3 }}>
                <Skeleton variant="text" width={90} height={30} animation="wave" />
                {["row1", "row2", "row3", "row4", "row5"].map((rowKey) => (
                  <Skeleton
                    key={`user-item-skeleton-${rowKey}`}
                    variant="rectangular"
                    height={36}
                    sx={{ borderRadius: 1, mt: 1 }}
                    animation="wave"
                  />
                ))}
              </Card>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Card sx={{ p: 3, mb: 3 }}>
                <Skeleton variant="text" width={100} height={30} animation="wave" />
                {["d1", "d2", "d3", "d4", "d5", "d6"].map((rowKey) => (
                  <Skeleton
                    key={`user-details-skeleton-${rowKey}`}
                    variant="text"
                    height={28}
                    animation="wave"
                  />
                ))}
              </Card>
              <Card sx={{ p: 3 }}>
                <Skeleton variant="text" width={110} height={30} animation="wave" />
                <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, mt: 2 }} />
                <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, mt: 1 }} />
              </Card>
            </Grid>
          </Grid>
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!quotation) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <Card sx={{ p: 4, maxWidth: 600, mx: "auto", textAlign: "center" }}>
            <SoftTypography variant="h5" mb={2}>
              Quotation not found
            </SoftTypography>
            <SoftButton
              variant="outlined"
              color="info"
              onClick={() => navigate("/user/quotations/listing")}
            >
              Back to Quotations
            </SoftButton>
          </Card>
        </SoftBox>
      </DashboardLayout>
    );
  }

  const statusEntry = QUOTATION_STATUS_LABELS.find(
    (entry) => Number(entry.value) === Number(quotation.status)
  );
  const statusLabel = statusEntry?.label || `Status ${quotation.status}`;
  const statusSxMap = {
    0: { bgcolor: "#f3f4f6", color: "#6b7280", borderRadius: 6 }, // Draft
    1: { bgcolor: "#dbeafe", color: "#1d4ed8", borderRadius: 6 }, // Sent
    2: { bgcolor: "#dcfce7", color: "#15803d", borderRadius: 6 }, // Accepted
    3: { bgcolor: "#dcfce7", color: "#15803d", borderRadius: 6 }, // Paid
    4: { bgcolor: "#fee2e2", color: "#b91c1c", borderRadius: 6 }, // Overdue
    5: { bgcolor: "#f3f4f6", color: "#6b7280", borderRadius: 6 }, // Cancelled
  };
  const statusSx = statusSxMap[Number(quotation.status)] || {
    bgcolor: "#f3f4f6",
    color: "#6b7280",
    borderRadius: 6,
  };

  const products = Array.isArray(quotation.items) ? quotation.items : [];
  const subtotal = products.reduce(
    (sum, p) => sum + Number(p.price || p.rate * p.quantity || 0),
    0
  );
  const docTotal = Number(quotation.amount || subtotal);
  const quotationStatus = Number(quotation.status);
  const paymentMode = quotation.paymentMode || "-";
  const hasInvoice = Boolean(quotation.invoiceId);
  const showInvoiceDetails = hasInvoice;
  const showPaymentOptions = quotationStatus === 2 && !hasInvoice;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        mt={2}
      >
        <Grid item direction={"column"}>
          <Link to="/user/quotations/listing">
            <SoftTypography variant="h5" fontSize="medium" component="a">
              Go Back
            </SoftTypography>
          </Link>
        </Grid>
      </Stack>

      <SoftBox py={2}>
        <Grid container spacing={3} justifyContent="center">
          {/* ── Left: main info ─────────────────────────────────────────── */}
          <Grid item xs={12} lg={8}>
            {/* Header card */}
            <Card sx={{ p: 3, mb: 3 }}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <SoftTypography variant="h4" fontWeight="bold">
                    Quotation #{quotation.id}
                  </SoftTypography>
                  <SoftTypography variant="body2" color="text.secondary" mt={0.5}>
                    Created: {formatDate(quotation.createdAt)}
                    {quotation.updatedAt !== quotation.createdAt && (
                      <> &bull; Updated: {formatDate(quotation.updatedAt)}</>
                    )}
                  </SoftTypography>
                </Box>
                <SoftBox
                  px={2}
                  py={0.2}
                  width={"fit-content"}
                  borderRadius={1}
                  fontSize="small"
                  fontWeight="bold"
                  textAlign="center"
                  sx={statusSx}
                >
                  {statusLabel}
                </SoftBox>
              </SoftBox>
            </Card>

            {/* Addresses section */}
            <Card sx={{ p: 3, mb: 3 }}>
              <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>
                Information
              </SoftTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoRow label="Organisation" value={quotation.customerName} />
                  <InfoRow label="Email" value={quotation.customerEmail} />
                  <InfoRow label="SO Number" value={quotation.soNumber} />
                </Grid>
                <Grid item xs={12} md={6}>
                  {quotation.billingAddress?.addressLineOne ? (
                    <>
                      <SoftTypography variant="caption" color="text.secondary">
                        Billing Address
                      </SoftTypography>
                      <SoftTypography variant="body2" mt={0.5}>
                        {quotation.billingAddress.addressLineOne}
                        {quotation.billingAddress.addressLineTwo && (
                          <>, {quotation.billingAddress.addressLineTwo}</>
                        )}
                        <br />
                        {[
                          quotation.billingAddress.city,
                          quotation.billingAddress.state,
                          quotation.billingAddress.zipCode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                        <br />
                        {quotation.billingAddress.country}
                      </SoftTypography>
                    </>
                  ) : (
                    <SoftTypography variant="body2" color="text.secondary">
                      No billing address provided
                    </SoftTypography>
                  )}
                </Grid>
              </Grid>
            </Card>

            {/* Products */}
            <Card sx={{ p: 3, mb: 3 }}>
              <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>
                Items
              </SoftTypography>
              <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                <Table size="small">
                  <EnhancedTableHead
                    headCells={[
                      { id: "no", label: "#", align: "left" },
                      { id: "name", label: "Description", align: "left" },
                      { id: "qty", label: "Qty", align: "center" },
                      { id: "rate", label: "Unit Price", align: "right" },
                      { id: "amount", label: "Line Total", align: "right" },
                    ]}
                  />
                  <TableBody>
                    {products.map((p, idx) => (
                      <TableRow key={p.id || idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <strong>{p.name || "-"}</strong>
                          {p.description && (
                            <Box fontSize="12px" color="text.secondary">
                              {p.description}
                            </Box>
                          )}
                          {p.lineKind === "subscription" && (
                            <Box fontSize="12px" color="text.secondary">
                              Subscription
                              {p.subscriptionTerm ? ` · billed ${p.subscriptionTerm}` : ""}
                              {subscriptionTermText(p)}
                              {p.subscriptionLocationName ? ` · ${p.subscriptionLocationName}` : ""}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">{p.quantity || 1}</TableCell>
                        <TableCell align="right">{formatCurrency(p.rate)}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(Number(p.price || p.rate * p.quantity || 0))}
                        </TableCell>
                      </TableRow>
                    ))}
                    {products.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <SoftTypography variant="button" color="text">
                            No items
                          </SoftTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals */}
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Box minWidth={260}>
                  <InfoRow label="Subtotal" value={formatCurrency(subtotal)} />
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    pt={1}
                    mt={0.5}
                    borderTop="2px solid"
                    borderColor="divider"
                  >
                    <SoftTypography variant="h6" fontWeight="bold">
                      Total
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      {formatCurrency(docTotal)}
                    </SoftTypography>
                  </Box>
                </Box>
              </Box>
            </Card>

            {/* Note */}
            {quotation.note && (
              <Card sx={{ p: 3 }}>
                <SoftTypography variant="h6" fontWeight="bold" mb={1}>
                  Notes
                </SoftTypography>
                <SoftTypography variant="body2" color="text.secondary">
                  {quotation.note}
                </SoftTypography>
              </Card>
            )}
          </Grid>

          {/* ── Right: meta + actions ───────────────────────────────────── */}
          <Grid item xs={12} lg={4}>
            {/* Order Meta */}
            <Card sx={{ p: 3, mb: 3 }}>
              <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>
                Details
              </SoftTypography>
              <InfoRow label="Quote #" value={quotation.id} />
              <InfoRow label="Due Date" value={formatDate(quotation.dueDate)} />
              <InfoRow label="PO Number" value={quotation.customerPoNumber} />
              <InfoRow label="Payment Terms" value={quotation.paymentTerms} />
              <InfoRow label="Tracking No." value={quotation.trackingNumber} />
              <InfoRow label="Ship Via" value={quotation.shipVia} />
            </Card>

            {/* Actions */}
            {Number(quotation.status) === 1 && (
              <Card sx={{ p: 3 }}>
                <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>
                  Actions
                </SoftTypography>
                <SoftBox display="flex" flexDirection="column" gap={1.5}>
                  <SoftButton
                    variant="gradient"
                    color="success"
                    fullWidth
                    onClick={handleAcceptClick}
                    disabled={accepting}
                  >
                    {accepting ? "Accepting..." : "Accept Quotation"}
                  </SoftButton>
                </SoftBox>
              </Card>
            )}
            {/* Payment Section (Visible after acceptance) */}
            {showPaymentOptions && (
              <Card sx={{ p: 3, mt: 3, border: "1px solid", borderColor: "success.main" }}>
                <SoftTypography variant="h6" fontWeight="bold" color="success" mb={1.5}>
                  Payment Options
                </SoftTypography>
                <SoftTypography variant="body2" color="text.secondary" mb={2}>
                  This quotation has been accepted. Please choose a payment method to proceed.
                </SoftTypography>
                <SoftBox display="flex" flexDirection="column" gap={1.5}>
                  <SoftButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    onClick={handleStripePay}
                    disabled={processingPayment}
                  >
                    {processingPayment ? "Processing..." : "Pay Online (Stripe)"}
                  </SoftButton>
                  <SoftButton
                    variant="outlined"
                    color="info"
                    fullWidth
                    onClick={handlePOPayClick}
                    disabled={processingPayment}
                  >
                    Pay with PO / Check
                  </SoftButton>
                </SoftBox>
              </Card>
            )}

            {showInvoiceDetails && (
              <Card sx={{ p: 3, mt: 3, border: "1px solid", borderColor: "info.main" }}>
                <SoftTypography variant="h6" fontWeight="bold" color="info" mb={1.5}>
                  Invoice Details
                </SoftTypography>
                <SoftTypography variant="body2" color="text.secondary" mb={2}>
                  An invoice has been generated for this quotation.
                </SoftTypography>
                <InfoRow label="Invoice ID" value={quotation.invoiceId} />
                <InfoRow label="Invoice #" value={quotation.invoiceSerialNumber} />
                <InfoRow
                  label="Invoice Status"
                  value={
                    INVOICE_STATUS_LABELS.find(
                      (l) => Number(l.value) === Number(quotation.invoice?.status)
                    )?.label || "N/A"
                  }
                />
                <InfoRow label="Payment Mode" value={paymentMode} />
                <InfoRow label="PO Number" value={quotation.customerPoNumber} />
                <InfoRow label="Payment Terms" value={quotation.paymentTerms} />
                <InfoRow label="Amount" value={formatCurrency(docTotal)} />
                {quotation?.invoice?.invoicePdf && (
                  <SoftBox mt={2}>
                    <SoftButton variant="gradient" color="info" fullWidth onClick={handleViewPdf}>
                      View Invoice Details
                    </SoftButton>
                  </SoftBox>
                )}
              </Card>
            )}
          </Grid>
        </Grid>
      </SoftBox>

      <Dialog
        open={showConfirmModal}
        onClose={handleConfirmClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Accept Quotation?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to accept this quotation? This action will change the status to
            Accepted and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SoftButton onClick={handleConfirmClose} color="secondary" variant="outlined">
            Cancel
          </SoftButton>
          <SoftButton onClick={handleAcceptSubmit} color="info" variant="gradient" autoFocus>
            Confirm Accept
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* PA with PO/Check Dialog */}
      <Dialog
        open={showPODialog}
        onClose={() => !processingPayment && setShowPODialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm with PO / Check</DialogTitle>
        <DialogContent>
          <SoftBox mt={2} display="flex" flexDirection="column" gap={2}>
            <SoftBox>
              <SoftTypography
                fontSize="small"
                fontWeight="bold"
                letterSpacing={0.3}
                variant="h5"
                mb={0.5}
              >
                Customer PO Number
              </SoftTypography>
              <TextField
                placeholder="Customer PO Number"
                name="poNumber"
                fullWidth
                required
                value={poForm.poNumber}
                onChange={handlePOFormChange}
                disabled={processingPayment}
              />
            </SoftBox>
            <SoftBox>
              <SoftTypography
                fontSize="small"
                fontWeight="bold"
                letterSpacing={0.3}
                variant="h5"
                mb={0.5}
              >
                Payment Terms
              </SoftTypography>
              <TextField
                placeholder="e.g. Net 30"
                name="paymentTerms"
                fullWidth
                value={poForm.paymentTerms}
                onChange={handlePOFormChange}
                disabled={processingPayment}
              />
            </SoftBox>
            <SoftBox>
              <SoftTypography
                fontSize="small"
                fontWeight="bold"
                letterSpacing={0.3}
                variant="h5"
                mb={0.5}
              >
                Note / Remarks
              </SoftTypography>
              <TextField
                placeholder="Note / Remarks"
                name="note"
                fullWidth
                multiline
                rows={3}
                value={poForm.note}
                onChange={handlePOFormChange}
                disabled={processingPayment}
              />
            </SoftBox>
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <SoftButton
            onClick={() => setShowPODialog(false)}
            color="secondary"
            variant="outlined"
            disabled={processingPayment}
          >
            Cancel
          </SoftButton>
          <SoftButton
            onClick={handlePOSubmit}
            color="info"
            variant="gradient"
            disabled={processingPayment}
          >
            {processingPayment ? "Processing..." : "Generate Invoice"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default UserQuotationDetails;
